import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Checkbox,
    Flex,
    HStack,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Portal,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
} from '@chakra-ui/react';
import type { ElementType } from 'react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { FiExternalLink } from 'react-icons/fi';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { TbArchive, TbArchiveOff, TbFileCheck, TbReceiptRefund, TbTruckReturn, TbTruck } from 'react-icons/tb';
import type { InvoiceEntry } from '../../../hooks/invoice/useInvoicesFeed';
import { InvoiceListStatusBadge } from '../../../components/invoice/InvoiceListStatusBadge';
import { getInvoiceListStatusTooltip } from '../../../components/invoice/invoiceListStatusConfig';
import BulkActionBar, { type BulkActionBarItem } from '../../../components/BulkActionBar';
import OrderHoverCheckbox from '../../../components/orders/OrderHoverCheckbox';
import Pagination from '../../../components/Pagination';
import ResizableTh from '../../../components/ResizableTh';
import {
    getBulkCheckboxLeft,
    HOME_CONTENT_PL_WITH_BULK,
    RO_BULK_CHECKBOX_GUTTER,
} from '../../../constants/pageLayout';
import { formatPrice } from '../../../functions';
import { getInvoiceVendorDisplayName } from '../../../utils/invoiceVendorDisplay';
import { getInvoiceNexIdentifierUi } from '../../../utils/invoiceNexIdentifierUi';
import { getInvoiceReasonBadges } from './invoicesListPageUtils';
import type { InvoicesListPageModel } from './useInvoicesListPage';

export function InvoicesListTable({ page }: { page: InvoicesListPageModel }) {
    const {
        invoicesData,
        isLoading,
        isRefetching,
        error,
        pageInvoices,
        searchQuery,
        selectedInvoiceIds,
        showArchived,
        allPageSelected,
        isPageSelectionIndeterminate,
        clearSelection,
        tableSettingsSx,
        colWidths,
        updateColWidth,
        scrollContainerRef,
        showRightShadow,
        archiveInvoicesMutation,
        nexReconciliationIdentifierMode,
        handleRowClick,
        toggleSelectAllOnPage,
        toggleInvoiceSelected,
        handleToggleArchiveInvoice,
        handleBulkAction,
        currentPage,
        setCurrentPage,
        totalPages,
        hasNextPage,
        onReasonHelpOpen,
        onStatusHelpOpen,
    } = page;

    const identifierUi = getInvoiceNexIdentifierUi(nexReconciliationIdentifierMode);
    const bulkActions: BulkActionBarItem[] = [
        {
            id: showArchived ? 'unarchive' : 'archive',
            label: showArchived ? 'Unarchive' : 'Archive',
            icon: (showArchived ? TbArchiveOff : TbArchive) as ElementType,
        },
    ];

    return (
        <Box flex={1} display="flex" flexDirection="column" overflow="hidden" minH={0}>
            {isLoading && !invoicesData && !error ? (
                <Flex justify="center" align="center" h="400px">
                    <Spinner size="xl" color="blue.500" />
                </Flex>
            ) : error ? (
                <Alert status="error">
                    <AlertIcon />
                    Failed to load invoices. Please try again.
                </Alert>
            ) : pageInvoices.length > 0 ? (
                <>
                    <Box position="relative" flex={1} minH={0} display="flex" flexDirection="column">
                        <Box
                            ref={scrollContainerRef}
                            flex={1}
                            minH={0}
                            overflow="auto"
                            borderTop="1px solid"
                            borderColor="gray.200"
                            sx={{
                                ...tableSettingsSx,
                                overscrollBehavior: 'contain',
                                '&::-webkit-scrollbar': {
                                    width: '8px',
                                    height: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: '#cbd5e0',
                                    borderRadius: '4px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    background: '#a0aec0',
                                },
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e0 transparent',
                                '& table': {
                                    width: 'max-content !important',
                                    minWidth: '100%',
                                },
                                '& thead': {
                                    zIndex: '3 !important',
                                },
                                '& th': {
                                    ...(tableSettingsSx['& th'] || {}),
                                    borderBottomWidth: '0 !important',
                                    boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
                                    bg: 'gray.50',
                                },
                                '& th:first-of-type': {
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 6,
                                    bg: 'gray.50',
                                    boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
                                },
                                '& td:first-of-type': {
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 2,
                                    bg: 'white',
                                },
                                '& tbody tr:hover td:first-of-type': {
                                    bg: 'gray.50',
                                },
                                '& th:last-of-type': {
                                    position: 'sticky',
                                    right: 0,
                                    zIndex: 5,
                                    bg: 'gray.50',
                                },
                                '& td:last-of-type': {
                                    position: 'sticky',
                                    right: 0,
                                    zIndex: 2,
                                    bg: 'white',
                                },
                                '& tbody tr:hover td:last-of-type': {
                                    bg: 'gray.50',
                                },
                                '& tr:hover .order-row-checkbox': {
                                    opacity: '1 !important',
                                    pointerEvents: 'auto !important',
                                },
                                '& tr[data-selected="true"] td:first-of-type': {
                                    bg: 'blue.50',
                                },
                                '& tr[data-selected="true"]:hover td:first-of-type': {
                                    bg: 'blue.100',
                                },
                                '& tr[data-selected="true"] td:last-of-type': {
                                    bg: 'blue.50',
                                },
                                '& tr[data-selected="true"]:hover td:last-of-type': {
                                    bg: 'blue.100',
                                },
                            }}
                        >
                            <Table
                                variant="simple"
                                size="sm"
                                minW="100%"
                                sx={
                                    Object.keys(colWidths).length > 0
                                        ? {
                                              tableLayout: 'fixed',
                                              '& td': {
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                              },
                                          }
                                        : undefined
                                }
                            >
                                <Thead position="sticky" top={0} zIndex={1}>
                                    <Tr bg="gray.50">
                                        <ResizableTh
                                            columnKey="roNumber"
                                            storedWidth={colWidths.roNumber}
                                            onResize={updateColWidth}
                                            minWidth={120}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            pl={HOME_CONTENT_PL_WITH_BULK}
                                            pr={3}
                                            position="relative"
                                            textTransform="uppercase"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        >
                                            <Box
                                                position="absolute"
                                                left={getBulkCheckboxLeft(HOME_CONTENT_PL_WITH_BULK)}
                                                top="50%"
                                                transform="translateY(-50%)"
                                                w={RO_BULK_CHECKBOX_GUTTER}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Checkbox
                                                    aria-label="Select all invoices on this page"
                                                    isChecked={allPageSelected}
                                                    isIndeterminate={isPageSelectionIndeterminate}
                                                    isDisabled={pageInvoices.length === 0}
                                                    size="sm"
                                                    colorScheme="blue"
                                                    sx={{
                                                        '& .chakra-checkbox__control': {
                                                            width: '14px',
                                                            height: '14px',
                                                        },
                                                        '& .chakra-checkbox__icon': {
                                                            fontSize: '9px',
                                                        },
                                                    }}
                                                    onChange={() => toggleSelectAllOnPage()}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </Box>
                                            {identifierUi.listTableColumnTitle}
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="shopLocation"
                                            storedWidth={colWidths.shopLocation}
                                            onResize={updateColWidth}
                                            minWidth={140}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        >
                                            Shop location
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="uploadDate"
                                            storedWidth={colWidths.uploadDate}
                                            onResize={updateColWidth}
                                            minWidth={110}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        >
                                            Upload Date
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="invoiceType"
                                            storedWidth={colWidths.invoiceType}
                                            onResize={updateColWidth}
                                            minWidth={120}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        >
                                            Invoice Type
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="invoiceNumber"
                                            storedWidth={colWidths.invoiceNumber}
                                            onResize={updateColWidth}
                                            minWidth={100}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        >
                                            Invoice #
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="vendor"
                                            storedWidth={colWidths.vendor}
                                            onResize={updateColWidth}
                                            minWidth={100}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        >
                                            Vendor
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="totalParts"
                                            storedWidth={colWidths.totalParts}
                                            onResize={updateColWidth}
                                            minWidth={90}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        >
                                            Total Parts
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="amount"
                                            storedWidth={colWidths.amount}
                                            onResize={updateColWidth}
                                            minWidth={90}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        >
                                            Amount
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="status"
                                            storedWidth={colWidths.status}
                                            onResize={updateColWidth}
                                            minWidth={120}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        >
                                            <HStack spacing={1} align="center">
                                                <Text
                                                    as="span"
                                                    color="inherit"
                                                    fontSize="inherit"
                                                    fontWeight="inherit"
                                                >
                                                    Status
                                                </Text>
                                                <Tooltip
                                                    label="What do these statuses mean?"
                                                    hasArrow
                                                    placement="top"
                                                >
                                                    <IconButton
                                                        aria-label="Open explanations for invoice statuses"
                                                        icon={<InfoOutlineIcon boxSize={3} />}
                                                        size="xs"
                                                        variant="ghost"
                                                        color="gray.500"
                                                        minW={4}
                                                        h={4}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onStatusHelpOpen();
                                                        }}
                                                    />
                                                </Tooltip>
                                            </HStack>
                                        </ResizableTh>
                                        <ResizableTh
                                            columnKey="reason"
                                            storedWidth={colWidths.reason}
                                            onResize={updateColWidth}
                                            minWidth={120}
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            px={3}
                                            textTransform="uppercase"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        >
                                            <HStack spacing={1} align="center">
                                                <Text
                                                    as="span"
                                                    color="inherit"
                                                    fontSize="inherit"
                                                    fontWeight="inherit"
                                                >
                                                    Reason
                                                </Text>
                                                <Tooltip
                                                    label="What do these flags mean?"
                                                    hasArrow
                                                    placement="top"
                                                >
                                                    <IconButton
                                                        aria-label="Open explanations for invoice flag reasons"
                                                        icon={<InfoOutlineIcon boxSize={3} />}
                                                        size="xs"
                                                        variant="ghost"
                                                        color="gray.500"
                                                        minW={4}
                                                        h={4}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onReasonHelpOpen();
                                                        }}
                                                    />
                                                </Tooltip>
                                            </HStack>
                                        </ResizableTh>
                                        <Th
                                            color="gray.500"
                                            fontSize="11px"
                                            fontWeight="semibold"
                                            letterSpacing="wider"
                                            py={2.5}
                                            pl={3}
                                            pr={5}
                                            w="60px"
                                            minW="60px"
                                            textAlign="right"
                                            borderBottomWidth="1px"
                                            borderColor="gray.200"
                                        />
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {pageInvoices.map((invoice: InvoiceEntry, rowIndex: number) => {
                                        const isSelected = selectedInvoiceIds.has(invoice._id);
                                        return (
                                        <Tr
                                            key={invoice._id}
                                            data-selected={isSelected ? 'true' : undefined}
                                            bg={isSelected ? 'blue.50' : undefined}
                                            _hover={{
                                                bg: isSelected ? 'blue.100' : 'gray.50',
                                                cursor: 'pointer',
                                            }}
                                            onClick={(e) => handleRowClick(invoice._id, e, rowIndex)}
                                        >
                                            <Td
                                                py={3}
                                                pl={HOME_CONTENT_PL_WITH_BULK}
                                                pr={3}
                                                position="relative"
                                                borderBottomWidth="1px"
                                                borderColor="gray.100"
                                            >
                                                <Box
                                                    position="absolute"
                                                    left={getBulkCheckboxLeft(HOME_CONTENT_PL_WITH_BULK)}
                                                    top="50%"
                                                    transform="translateY(-50%)"
                                                    w={RO_BULK_CHECKBOX_GUTTER}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <OrderHoverCheckbox
                                                        orderId={invoice._id}
                                                        rowIndex={rowIndex}
                                                        isChecked={isSelected}
                                                        onToggle={toggleInvoiceSelected}
                                                    />
                                                </Box>
                                                <Text
                                                    color={
                                                        invoice.roNumber.length === 0
                                                            ? 'red.500'
                                                            : 'gray.900'
                                                    }
                                                    fontSize="sm"
                                                    fontWeight="medium"
                                                    fontStyle={
                                                        invoice.roNumber.length === 0
                                                            ? 'italic'
                                                            : 'normal'
                                                    }
                                                    noOfLines={1}
                                                >
                                                    {invoice.roNumber.length === 0
                                                        ? identifierUi.listTableMissingIdentifier
                                                        : invoice.roNumber}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <Text color="gray.900" fontSize="sm">
                                                    {invoice.locationTitle?.trim()
                                                        ? invoice.locationTitle
                                                        : '—'}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <VStack spacing={0} align="flex-start">
                                                    <Text color="gray.900" fontSize="sm" fontWeight="bold">
                                                        {new Date(invoice.timestamp).toLocaleTimeString([], {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        })}
                                                    </Text>
                                                    <Text color="gray.600" fontSize="xs">
                                                        {new Date(invoice.timestamp).toLocaleDateString()}
                                                    </Text>
                                                </VStack>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <Badge
                                                    colorScheme={
                                                        invoice.invoiceType === 'received'
                                                            ? 'blue'
                                                            : invoice.invoiceType === 'sublet'
                                                              ? 'green'
                                                            : invoice.invoiceType === 'return_slip'
                                                              ? 'yellow'
                                                              : 'red'
                                                    }
                                                    variant="subtle"
                                                    px={2}
                                                    py={1}
                                                    fontSize="xs"
                                                    fontWeight="medium"
                                                >
                                                    <HStack spacing={1}>
                                                        <Icon
                                                            as={
                                                                (invoice.invoiceType === 'received'
                                                                    ? TbFileCheck
                                                                    : invoice.invoiceType === 'returned'
                                                                      ? TbReceiptRefund
                                                                      : invoice.invoiceType === 'sublet'
                                                                        ? TbTruck
                                                                      : TbTruckReturn) as React.ElementType
                                                            }
                                                            boxSize={3.5}
                                                        />
                                                        <Text fontSize="xs">
                                                            {invoice.invoiceType === 'received'
                                                                ? 'Received'
                                                                : invoice.invoiceType === 'returned'
                                                                  ? 'Credited'
                                                                  : invoice.invoiceType === 'sublet'
                                                                    ? 'Sublet'
                                                                  : 'Return Slip'}
                                                        </Text>
                                                    </HStack>
                                                </Badge>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <Text color="gray.900" fontSize="sm">
                                                    {invoice.invoiceNumber}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <Text color="gray.900" fontSize="sm">
                                                    {getInvoiceVendorDisplayName(invoice)}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <Text color="gray.900" fontSize="sm">
                                                    {invoice.totalParts}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <Text color="gray.900" fontSize="sm" fontWeight="medium">
                                                    {formatPrice(invoice.orderTotal)}
                                                </Text>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <Tooltip
                                                    label={getInvoiceListStatusTooltip(invoice.status)}
                                                    hasArrow
                                                    placement="top"
                                                >
                                                    <Box as="span" display="inline-block" cursor="help">
                                                        <InvoiceListStatusBadge status={invoice.status} />
                                                    </Box>
                                                </Tooltip>
                                            </Td>
                                            <Td py={3} px={3} borderBottomWidth="1px" borderColor="gray.100">
                                                <HStack spacing={1} align="center" flexWrap="wrap">
                                                    {getInvoiceReasonBadges(
                                                        invoice,
                                                        nexReconciliationIdentifierMode,
                                                    ).map(({ key, label, colorScheme, tooltip }) => (
                                                        <Tooltip
                                                            key={key}
                                                            label={tooltip}
                                                            hasArrow
                                                            placement="top"
                                                        >
                                                            <Badge
                                                                colorScheme={colorScheme}
                                                                variant="subtle"
                                                                px={2}
                                                                py={1}
                                                                fontSize="xs"
                                                                fontWeight="semibold"
                                                                cursor="help"
                                                            >
                                                                {label}
                                                            </Badge>
                                                        </Tooltip>
                                                    ))}
                                                </HStack>
                                            </Td>
                                            <Td
                                                py={3}
                                                pr={5}
                                                pl={1}
                                                textAlign="right"
                                                w="60px"
                                                minW="60px"
                                                borderBottomWidth="1px"
                                                borderColor="gray.100"
                                            >
                                                <Menu strategy="fixed">
                                                    <MenuButton
                                                        as={IconButton}
                                                        aria-label="Options"
                                                        icon={
                                                            <Icon
                                                                as={
                                                                    HiOutlineDotsVertical as React.ElementType
                                                                }
                                                                w={4}
                                                                h={4}
                                                            />
                                                        }
                                                        size="xs"
                                                        variant="outline"
                                                        colorScheme="gray"
                                                        borderRadius="lg"
                                                        borderColor="gray.300"
                                                        _hover={{
                                                            bg: 'gray.50',
                                                            borderColor: 'gray.400',
                                                        }}
                                                        _active={{
                                                            bg: 'gray.100',
                                                            borderColor: 'gray.400',
                                                        }}
                                                        _focus={{ boxShadow: 'outline' }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <Portal>
                                                        <MenuList
                                                            py={1}
                                                            minW="200px"
                                                            zIndex={1400}
                                                            bg="white"
                                                            boxShadow="none"
                                                        >
                                                            <MenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    window.open(
                                                                        `/invoices/${invoice._id}`,
                                                                        '_blank',
                                                                    );
                                                                }}
                                                                fontSize="sm"
                                                                px={3}
                                                                py={2}
                                                                color="gray.700"
                                                                _hover={{ bg: '#E6F1FD', color: '#0071E3' }}
                                                            >
                                                                <HStack spacing={2}>
                                                                    <Icon
                                                                        as={
                                                                            FiExternalLink as React.ElementType
                                                                        }
                                                                        boxSize={4}
                                                                        color="gray.500"
                                                                    />
                                                                    <Text>Open in New Tab</Text>
                                                                </HStack>
                                                            </MenuItem>
                                                            <MenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    void handleToggleArchiveInvoice(invoice);
                                                                }}
                                                                fontSize="sm"
                                                                px={3}
                                                                py={2}
                                                                color="gray.700"
                                                                _hover={{ bg: '#E6F1FD', color: '#0071E3' }}
                                                                isDisabled={
                                                                    archiveInvoicesMutation.isPending
                                                                }
                                                            >
                                                                <HStack spacing={2}>
                                                                    <Icon
                                                                        as={
                                                                            (invoice.archived
                                                                                ? TbArchiveOff
                                                                                : TbArchive) as React.ElementType
                                                                        }
                                                                        boxSize={4}
                                                                        color="gray.500"
                                                                    />
                                                                    <Text>
                                                                        {invoice.archived
                                                                            ? 'Unarchive Invoice'
                                                                            : 'Archive Invoice'}
                                                                    </Text>
                                                                </HStack>
                                                            </MenuItem>
                                                        </MenuList>
                                                    </Portal>
                                                </Menu>
                                            </Td>
                                        </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                        {showRightShadow && (
                            <Box
                                position="absolute"
                                right={0}
                                top={0}
                                bottom={0}
                                w="48px"
                                bgGradient="linear(to-r, rgba(255,255,255,0), rgba(255,255,255,0.85))"
                                pointerEvents="none"
                                zIndex={4}
                            />
                        )}
                        {selectedInvoiceIds.size > 0 && (
                            <BulkActionBar
                                selectedCount={selectedInvoiceIds.size}
                                actions={bulkActions}
                                onClearSelection={clearSelection}
                                onAction={handleBulkAction}
                                isLoading={archiveInvoicesMutation.isPending}
                                shortcutEnabled
                            />
                        )}
                    </Box>
                    <Box flexShrink={0} borderTop="1px solid" borderColor="gray.200">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            isLoading={isLoading || isRefetching}
                            hasNextPage={hasNextPage}
                        />
                    </Box>
                </>
            ) : (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flex={1}
                    borderTop="1px solid"
                    borderColor="gray.200"
                >
                    <VStack spacing={2}>
                        <Text fontSize="md" color="gray.500">
                            {searchQuery ? 'No matching invoices found' : 'No invoices found'}
                        </Text>
                        <Text fontSize="sm" color="gray.400">
                            {searchQuery
                                ? 'Try a different search term'
                                : 'Invoices will appear here once they are processed'}
                        </Text>
                    </VStack>
                </Box>
            )}
        </Box>
    );
}
